<template>
    <div class="senderSettings">
        <b-form-group :label="$t('realNameLabel')">
            <b-input v-model="realName" :disabled="!$store.state.settings.allowchangerealname"
            :placeholder="$t('realNamePlaceholder')" :state="!$v.realName.$invalid" :debounce="debounce"></b-input>
            <b-form-invalid-feedback v-html="feedback('realName')"></b-form-invalid-feedback>
        </b-form-group>
        <b-form-group :label="$t('emailAddressesLabel')">
            <b-textarea v-model="emailAddress" :disabled="!$store.state.settings.allowchangeemailaddress"
            :placeholder="$t('emailAddressesPlaceholder')" :state="!$v.emailAddress.$invalid"
                    :debounce="debounce"></b-textarea>
            <b-form-invalid-feedback v-html="feedback('emailAddress')"></b-form-invalid-feedback>
        </b-form-group>
        <b-form-group :label="$t('autoBccLabel')" v-if="$store.state.settings.allowbccs">
            <b-input type="email" v-model="autoBcc" :placeholder="$t('autoBccPlaceholder')"
                    :state="!$v.autoBcc.$invalid" :debounce="debounce"></b-input>
            <b-form-invalid-feedback v-html="feedback('autoBcc')"></b-form-invalid-feedback>
        </b-form-group>
        <b-card bg-variant="light" :title="$t('signatureLabel')">
        <b-form-group>
            <b-form-group :label="$t('signatureMode')">
                <b-radio-group v-model="signatureMode" :options="signatureModes"></b-radio-group>
            </b-form-group>
            <b-form-group v-if="signatureMode === 'markdown'">
                <b-textarea v-model="signatureMarkdown" :placeholder="$t('signaturePlaceholder')"
                        :state="!$v.signatureMarkdown.$invalid" :debounce="debounce" max-rows="6" rows="3"></b-textarea>
                <b-form-invalid-feedback v-html="feedback('signatureMarkdown')"></b-form-invalid-feedback>
                <div class="markdownPreviewWrap">
                    <div class="markdownPreviewTitle">{{$t('markdownPreviewTitle')}}</div>
                    <div class="markdownPreview" v-html="formattedMarkdown"></div>
                </div>
            </b-form-group>
            <b-form-group v-if="signatureMode === 'html'">
                <div class="wysiwygWrap" :class="{ invalid: $v.signatureHtml.$invalid }">
                    <wysiwyg v-model="signatureHtml"></wysiwyg>
                </div>
                <span class="sizeWarning" v-if="signatureHtml.length > 1000">
                    {{$t('signatureSizeWarning', { size: signatureHtml.length })}}
                </span>
                <span v-else>
                    {{$t('signatureSize', { size: signatureHtml.length })}}
                </span>
                <b-form-invalid-feedback v-html="feedback('signatureHtml')"></b-form-invalid-feedback>
            </b-form-group>
            <b-form-group v-if="signatureMode === 'text'">
                <b-textarea v-model="signature" :placeholder="$t('signaturePlaceholder')"
                        :state="!$v.signature.$invalid" :debounce="debounce"></b-textarea>
                <b-form-invalid-feedback v-html="feedback('signature')"></b-form-invalid-feedback>
            </b-form-group>
        </b-form-group>
        </b-card>

        <b-button :disabled="$v.$invalid || saving || !$store.state.settingsLoaded" variant="primary" @click="doSave">
            {{$t('save')}}
        </b-button>
        <font-awesome-icon :class="{ saveIcon: true, savingIcon: saving }" spin
                icon="spinner-third"></font-awesome-icon>
        <span class="error">{{saveError}}</span>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { EventBus } from '@/event-bus';
import emailValidator from 'email-validator';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Marked from 'marked';
import DOMPurify from 'dompurify';

const multiEmailValidator = (value, separator) => {
  return !value.split(separator).find(addr => {
    return !emailValidator.validate(addr);
  });
};

export default {
  name: 'sendingSettings',
  components: { FontAwesomeIcon },
  i18nOptions: { keyPrefix: 'settings.sending' },
  data: function() {
    return {
      realName: '',
      realNameServerValidated: '',
      emailAddress: '',
      emailAddressServerValidated: '',
      autoBcc: '',
      autoBccServerValidated: '',
      signature: '',
      signatureServerValidated: '',
      signatureHtml: '',
      signatureHtmlServerValidated: '',
      signatureMarkdown: '',
      signatureMarkdownServerValidated: '',
      saving: false,
      debounce: 100,
      saveError: '',
      signatureMode: ''
    };
  },
  validations: {
    realName: { required,
      serverValidateRealName(value, vm) {
        return !vm.realNameServerValidated;
      }
    },
    emailAddress: {
      required,
      email(value) {
        return multiEmailValidator(value, /\n+/);
      },
      serverValidateEmailaddress(value, vm) {
        return !vm.emailAddressServerValidated;
      }
    },
    autoBcc: {
      email(value) {
        return multiEmailValidator(value, /\s*,\s*/);
      },
      serverValidateAutoBcc(value, vm) {
        return !vm.autoBccServerValidated;
      }
    },
    signature: {
      serverValidateSignature(value, vm) {
        return !vm.signatureServerValidated;
      }
    },
    signatureHtml: {
      serverValidateSignature(value, vm) {
        return !vm.signatureHtmlServerValidated;
      }
    },
    signatureMarkdown: {
      serverValidateSignature(value, vm) {
        return !vm.signatureMarkdownServerValidated;
      }
    }
  },
  created() {
    EventBus.$on('settingsLoaded', () => {
      this.initSettings();
    });
    Marked.setOptions({ breaks: true });
  },
  mounted() {
    this.initSettings();
  },
  watch: {
    async realName(newVal) {
      if (newVal) {
        this.realNameServerValidated = await this.serverValidate('validatesendername', { realname: newVal });
      }
    },
    async emailAddress(newVal) {
      if (newVal) {
        this.emailAddressServerValidated = await this.serverValidate('validatesenderemails',
          { emailaddresses: newVal });
      }
    },
    async autoBcc(newVal) {
      if (newVal) {
        this.autoBccServerValidated = await this.serverValidate('validatesenderbcc', { bcc: newVal });
      }
    },
    async signature(newVal) {
      if (newVal) {
        this.signatureServerValidated = await this.serverValidate('validatesendersignature',
          { mode: 'text', signature: newVal });
      }
    },
    async signatureHtml(newVal) {
      if (newVal) {
        console.log('server validate html sig');
        this.signatureHtmlServerValidated = await this.serverValidate('validatesendersignature',
          { mode: 'html', signature: newVal });
      }
    },
    async signatureMarkdown(newVal) {
      if (newVal) {
        this.signatureMarkdownServerValidated = await this.serverValidate('validatesendersignature',
          { mode: 'markdown', signature: newVal });
      }
    }
  },
  methods: {
    doSave() {
      let settings = {
        realname: this.realName,
        emailaddress: this.emailAddress,
        autobcc: this.autoBcc,
        signature: this.signature,
        signaturemode: this.signatureMode,
        signaturemarkdown: this.signatureMarkdown,
        signaturehtml: this.signatureHtml
      };
      this.saving = true;
      axios.patch('/rest/user/settings', settings)
        .then(d => {
          console.log(d);
          this.saveError = '';
        })
        .catch(e => {
          console.dir(e);
          this.saveError = this.$t('saveError');
          let saveErrorExtra = [];
          for (let err in e.response.data.errorfields) {
            switch (err) {
              case 'realname':
                this.realNameServerValidated = '<' + e.response.data.errorfields[err];
                break;
              case 'emailaddress':
                this.emailAddressServerValidated = '<' + e.response.data.errorfields[err];
                break;
              case 'autobcc':
                this.autoBccServerValidated = '<' + e.response.data.errorfields[err];
                break;
              case 'signature':
                this.signatureServerValidated = '<' + e.response.data.errorfields[err];
                break;
              case 'signaturemarkdown':
                this.signatureMarkdownServerValidated = '<' + e.response.data.errorfields[err];
                break;
              case 'signaturehtml':
                this.signatureHtmlServerValidated = '<' + e.response.data.errorfields[err];
                break;
              default:
                saveErrorExtra.push(this.$t(`saveErrors.${e.response.data.errorfields[err]}`, { field: err }));
                break;
            }
            if (saveErrorExtra.length > 0) {
              this.saveError = this.$t('saveErrorWithExtra', { extra: saveErrorExtra.join(', ') });
            } else {
              this.saveError = this.$t('saveError');
            }
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    serverValidate(rpcName, params) {
      return new Promise((resolve, reject) => {
        this.$RPC('settings', rpcName, params)
          .then(d => {
            console.log('server validated', d);
            resolve(d.result);
          })
          .catch(() => {
            resolve('');
          });
      });
    },
    feedback(field) {
      if (this.$v[field].required === false) {
        return this.$t('general:validation.required');
      } else if (this[`${field}ServerValidated`]) {
        const error = this[`${field}ServerValidated`];
        if (error === '.') {
          return this.$t('general:validation.server');
        } else if (error.substr(0, 1) === '<') {
          const error2 = this.$t(`saveErrors.${error.substr(1)}`, { field: this.$t('saveErrors.thisField') });
          return this.$t('general:validation.serverExtraInfo', { error: error2 });
        } else {
          return this.$t('general:validation.serverExtraInfo', { error });
        }
      }
      return '';
    },
    initSettings() {
      this.realName = this.$store.state.settings.realname;
      this.emailAddress = this.$store.getters.senderEmailAddresses;
      this.autoBcc = this.$store.state.settings.autobcc;
      this.signature = this.$store.state.settings.signature;
      this.signatureHtml = this.$store.state.settings.signaturehtml;
      this.signatureMarkdown = this.$store.state.settings.signaturemarkdown;
      this.signatureMode = this.$store.state.settings.signaturemode;
      if (this.signatureMode === '') {
        this.signatureMode = 'markdown';
        if (this.signatureMarkdown === '') {
          this.signatureMarkdown = this.signature;
        }
      }
    }
  },
  computed: {
    signatureModes() {
      return [
        { text: this.$t('signatureMarkdown'), value: 'markdown' },
        { text: this.$t('signatureHTML'), value: 'html' },
        { text: this.$t('signatureText'), value: 'text' }
      ];
    },
    formattedMarkdown() {
      return DOMPurify.sanitize(Marked(this.signatureMarkdown), { FORCE_BODY: true, FORBID_TAGS: ['title'] });
    }
  }
};
</script>

<style>
    @import "~vue-wysiwyg/dist/vueWysiwyg.css";
</style>
<style lang="scss">
    /* eslint-disable max-line */
    @import '../assets/bootstrap-style.scss';

    .senderSettings {
        .wysiwygWrap {
            outline: none;
            border: 1px solid $success;
            border-radius: 0.25rem;
            overflow: hidden;
            &:focus-within {
                box-shadow: 0 0 0 $input-focus-width rgba($success, .25);
            }
            .editr {
                border: none;
                background-repeat: no-repeat;
                background-position: top calc(5.375em + 0.1875rem) right calc(0.375em + 0.1875rem);
                background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
                background-image:
                        url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' \
                        width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' \
                        d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 \
                        1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
                .editr--content {
                    padding-right: 48px;
                }
            }
            &.invalid {
                border: 1px solid $danger;
                &:focus-within {
                    box-shadow: 0 0 0 $input-focus-width rgba($danger, .25);
                    // box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
                }

                .editr { // eslint-disable-next-line max-len
                    background-image: url("data:image/svg+xml,%3csvg \
                            xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' \
                    viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' \
                    d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' \
                    stroke='none'/%3e%3c/svg%3e");
                }
            }
        }
    }

    .markdownPreview {
        blockquote {
            margin: 1em 40px 1em 40px;
        }
    }
</style>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .markdownPreviewWrap {
        background-color: $dark;
        border: 1px solid $dark;
        border-radius: 3px;
        margin-top: 5px;
        color: $light;
        .markdownPreviewTitle {
            padding: 2px 10px;
        }
        .markdownPreview {
            background-color: $white;
            height: 6em;
            overflow-y: auto;
            color: $black;
            padding: 0 10px;

        }
    }
</style>
